import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const CookiePolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set document title and meta tags
    document.title = 'Cookie Policy | GrowthLink Digital Marketing';
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMetaTag('description', 'Learn about how GrowthLink uses cookies on our website. Understand cookie types, management options, and your privacy choices.');
    updateMetaTag('robots', 'noindex, nofollow');
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/cookie-policy`);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-darkGreen mb-6">Cookie Policy</h1>
          <p className="text-sm text-gray-600 mb-12">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                This Cookie Policy explains how GrowthLink ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website. This policy helps you understand what cookies are, how we use them, and your choices regarding their use. This policy is governed by Indian law and falls under the jurisdiction of Telangana.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">2. What Are Cookies?</h2>
              <p className="leading-relaxed mb-3">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p className="leading-relaxed">
                Cookies help us understand how you use our website, remember your preferences, and improve your overall experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">3. Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">3.1 Essential Cookies</h3>
                  <p className="leading-relaxed mb-2">
                    These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.
                  </p>
                  <p className="text-sm italic">Examples: Session cookies, security cookies, load balancing cookies</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">3.2 Performance Cookies</h3>
                  <p className="leading-relaxed mb-2">
                    These cookies collect information about how visitors use our website, such as which pages are visited most often and if users receive error messages.
                  </p>
                  <p className="text-sm italic">Examples: Google Analytics, page load time tracking</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">3.3 Functionality Cookies</h3>
                  <p className="leading-relaxed mb-2">
                    These cookies allow the website to remember choices you make (such as your language preference) and provide enhanced, personalized features.
                  </p>
                  <p className="text-sm italic">Examples: Language preferences, region settings, user interface customization</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">3.4 Targeting/Advertising Cookies</h3>
                  <p className="leading-relaxed mb-2">
                    These cookies are used to deliver advertisements more relevant to you and your interests. They also help measure the effectiveness of advertising campaigns.
                  </p>
                  <p className="text-sm italic">Examples: Facebook Pixel, Google Ads, retargeting cookies</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">4. First-Party and Third-Party Cookies</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-darkGreen mb-2">First-Party Cookies</h3>
                  <p className="leading-relaxed">
                    These are cookies set by GrowthLink directly. We use them to provide essential website functionality and analyze how you use our services.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-darkGreen mb-2">Third-Party Cookies</h3>
                  <p className="leading-relaxed">
                    These are cookies set by third-party services we use, such as analytics providers, social media platforms, and advertising networks. These third parties may use cookies to track your browsing activity across different websites.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">5. How We Use Cookies</h2>
              <p className="leading-relaxed mb-3">We use cookies for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>To enable essential website functionality and security</li>
                <li>To remember your preferences and settings</li>
                <li>To analyze website traffic and user behavior</li>
                <li>To improve our services and user experience</li>
                <li>To deliver relevant marketing content and advertisements</li>
                <li>To measure the effectiveness of our marketing campaigns</li>
                <li>To prevent fraud and enhance security</li>
                <li>To comply with legal obligations under Indian law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">6. Third-Party Services</h2>
              <p className="leading-relaxed mb-3">We use the following third-party services that may set cookies:</p>
              <div className="space-y-3">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Google Analytics</p>
                  <p className="text-sm">For website analytics and performance tracking</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Google Ads</p>
                  <p className="text-sm">For advertising and remarketing campaigns</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Facebook Pixel</p>
                  <p className="text-sm">For social media advertising and analytics</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">LinkedIn Insight Tag</p>
                  <p className="text-sm">For professional network advertising</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">7. Cookie Duration</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-darkGreen mb-2">Session Cookies</h3>
                  <p className="leading-relaxed">
                    These are temporary cookies that expire when you close your browser. They help us maintain your session as you navigate through our website.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-darkGreen mb-2">Persistent Cookies</h3>
                  <p className="leading-relaxed">
                    These cookies remain on your device for a set period or until you delete them. They help us remember your preferences and provide a personalized experience on return visits.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">8. Your Cookie Choices</h2>
              <p className="leading-relaxed mb-4">
                You have several options to manage or disable cookies:
              </p>
              
              <div className="space-y-4">
                <div className="bg-accent/10 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">Browser Settings</h3>
                  <p className="leading-relaxed mb-3">
                    Most web browsers allow you to control cookies through their settings. You can:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                    <li>Block all cookies</li>
                    <li>Block third-party cookies only</li>
                    <li>Delete cookies after each browsing session</li>
                    <li>Accept cookies from specific websites only</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">Browser-Specific Instructions</h3>
                  <ul className="space-y-2 text-sm">
                    <li><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
                    <li><strong>Mozilla Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
                    <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data</li>
                    <li><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-darkGreen mb-3">Opt-Out Tools</h3>
                  <p className="leading-relaxed mb-2">You can opt out of specific third-party cookies:</p>
                  <ul className="space-y-2 text-sm ml-4">
                    <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Google Analytics Opt-out Browser Add-on</a></li>
                    <li>Google Ads: <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Google Ads Settings</a></li>
                    <li>Facebook: <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Facebook Ad Preferences</a></li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm leading-relaxed">
                  <strong>Important:</strong> Please note that blocking or deleting cookies may impact your experience on our website. Some features may not function properly without cookies enabled.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">9. Mobile Device Identifiers</h2>
              <p className="leading-relaxed">
                When you access our website through a mobile device, we may collect mobile device identifiers and similar technologies. You can manage these through your device settings or by adjusting your app permissions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">10. Do Not Track Signals</h2>
              <p className="leading-relaxed">
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want your online activities tracked. Currently, there is no industry standard for how to respond to DNT signals. We do not currently respond to DNT signals, but we respect your privacy choices and provide options to manage cookies as described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">11. Updates to This Cookie Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">12. Legal Compliance</h2>
              <p className="leading-relaxed">
                This Cookie Policy complies with the Information Technology Act, 2000, and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, as applicable in India. This policy is subject to the jurisdiction of courts in Telangana, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">13. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg space-y-2">
                <p><strong>GrowthLink</strong></p>
                <p>Email: privacy@growthlink.com</p>
                <p>Phone: +91 XXX XXX XXXX</p>
                <p>Address: Telangana, India</p>
              </div>
            </section>

            <section className="bg-accent/10 p-6 rounded-lg mt-12">
              <h3 className="text-xl font-semibold text-darkGreen mb-3">Your Consent</h3>
              <p className="leading-relaxed text-sm">
                By continuing to use our website, you consent to our use of cookies as described in this Cookie Policy. If you do not agree with our use of cookies, you should adjust your browser settings accordingly or refrain from using our website.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CookiePolicy;
